/**
 * AdminsPage — Gestion des administrateurs.
 *
 * Liste les utilisateurs IBM i (DBPA0 IDLU) avec leur statut de compte
 * Supabase Auth. Permet de créer un compte admin (modale identique à
 * la modale technicien : email, prénom, nom, téléphone + sélecteur de
 * rôle) et de révoquer un compte existant.
 *
 * Le catalogue des rôles assignables et le rôle par défaut sont fournis
 * dynamiquement par GET /api/admins (RBAC configurable). Un caller ne
 * peut assigner qu'un rôle dont la `position` est >= à la sienne.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHasPermission, ROLE_LABELS } from '../SupabaseWrapper';
import {
  ShieldIcon,
  RefreshIcon,
  SearchIcon,
  UserIcon,
  CheckIcon,
  CloseIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon
} from '../icons';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { phoneInputStyles } from '../styles/phoneInput';
import { useViewport } from '../hooks/useViewport';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';

interface AdminsPageProps {
  refreshTrigger?: number;
}

interface AdminProfile {
  login: string;
  nomComplet: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  isSuperAdmin?: boolean;
  hasAccount: boolean;
  isActive: boolean;
  authorizedBy?: string | null;
  authorizedAt?: string | null;
  organizationName?: string | null;
  isAccountLevel?: boolean;
  isHomeOrg?: boolean;
}

type FilterStatus = 'all' | 'with_account' | 'without_account' | 'superadmin';

interface RoleInfo {
  name: string;
  display_name: string;
  color?: string | null;
  position: number;
}

const API_BASE = window.location.origin;

// Découpe un nom complet (ex. "Amine KHAMLICHI") en prénom / nom pour pré-remplir
// la modale de création. Heuristique positionnelle (1er mot = prénom, le reste = nom)
// car la casse des noms IBM i (company_name) n'est pas fiable. L'admin peut corriger.
function splitFullName(full: string): { first: string; last: string } {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

// Composant : badge de statut (declare avant AdminsPage pour reutilisation par AdminMobileCard)
interface StatusBadgeProps {
  color: string;
  bg: string;
  icon: React.ReactNode;
  label: string;
}
function StatusBadge({ color, bg, icon, label }: StatusBadgeProps) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 10px', backgroundColor: bg, color,
      borderRadius: '20px', fontSize: '12px', fontWeight: '500'
    }}>
      {icon}
      {label}
    </span>
  );
}

// Carte mobile pour AdminsPage (rendue par ResponsiveCardList sur < 768px)
function AdminMobileCard({
  admin,
  actionLoading,
  onRevoke,
  onCreate,
  canCreateAdmin,
  canRevoke,
  roleLabel,
}: {
  admin: AdminProfile;
  actionLoading: string | null;
  onRevoke: (login: string) => void;
  onCreate: (admin: AdminProfile) => void;
  canCreateAdmin: boolean;
  canRevoke: boolean;
  roleLabel: (role: string) => string;
}) {
  const cardBg = admin.isSuperAdmin ? '#fef3c7' : 'white';
  const isLoading = actionLoading === admin.login;

  return (
    <div style={{
      backgroundColor: cardBg,
      borderRadius: 12,
      padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      {/* Header : avatar + login + sublabel */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, flexShrink: 0,
          backgroundColor: admin.isSuperAdmin ? '#fde68a' : admin.hasAccount ? '#dcfce7' : '#f1f5f9',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {admin.isSuperAdmin
            ? <ShieldIcon size={18} color="#d97706" />
            : admin.hasAccount
              ? <CheckIcon size={18} color="#10b981" />
              : <UserIcon size={18} color="#94a3b8" />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {admin.login}
          </div>
          {admin.isSuperAdmin && (
            <span style={{ fontSize: 11, color: '#92400e', fontWeight: 500 }}>Super administrateur</span>
          )}
          {!admin.isSuperAdmin && admin.nomComplet && (
            <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {admin.nomComplet}
            </div>
          )}
        </div>
      </div>

      {/* Email */}
      {admin.email && (
        <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {admin.email}
        </div>
      )}

      {/* Statut badge — meme logique que desktop */}
      <div>
        {admin.isSuperAdmin ? (
          <StatusBadge color="#92400e" bg="#fef3c7" icon={<ShieldIcon size={12} />} label="Toujours autorisé" />
        ) : admin.hasAccount && admin.isActive ? (
          <StatusBadge color="#166534" bg="#dcfce7" icon={<CheckIcon size={12} />} label={`Compte (${roleLabel(admin.role)})`} />
        ) : admin.hasAccount && !admin.isActive ? (
          <StatusBadge color="#92400e" bg="#fef3c7" icon={<CloseIcon size={12} />} label="Désactivé" />
        ) : (
          <StatusBadge color="#475569" bg="#f1f5f9" icon={<UserPlusIcon size={12} />} label="Sans compte" />
        )}
      </div>

      {admin.isAccountLevel && admin.isHomeOrg === false && admin.organizationName && (
        <div style={{ marginTop: 4, fontSize: '11px', color: '#6366f1', fontWeight: 500 }}>
          Admin compte · {admin.organizationName}
        </div>
      )}

      {/* Action : meme logique que desktop, conditionnee par les permissions */}
      {!admin.isSuperAdmin && (
        <div>
          {admin.hasAccount && admin.isActive ? (
            canRevoke && !(admin.isAccountLevel && admin.isHomeOrg === false) && (
              <button
                onClick={() => onRevoke(admin.login)}
                disabled={isLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                  backgroundColor: '#fef2f2', color: '#dc2626',
                  border: '1px solid #fecaca', borderRadius: 6,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 500, opacity: isLoading ? 0.5 : 1,
                }}
              >
                <TrashIcon size={14} />
                Retirer l'accès
              </button>
            )
          ) : (
            canCreateAdmin && (
              <button
                onClick={() => onCreate(admin)}
                disabled={isLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                  backgroundColor: '#dbeafe', color: '#1e40af',
                  border: '1px solid #93c5fd', borderRadius: 6,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 500, opacity: isLoading ? 0.5 : 1,
                }}
              >
                <PlusIcon size={14} />
                {admin.hasAccount && !admin.isActive ? 'Réactiver le compte' : 'Créer un compte'}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function AdminsPage({ refreshTrigger = 0 }: AdminsPageProps) {
  // RBAC : permissions
  const canView = useHasPermission('admins.view');
  const canCreateAdmin = useHasPermission('admins.create_account', 'organization');
  const canRevoke = useHasPermission('admins.revoke');

  const { isPhone } = useViewport();

  const [admins, setAdmins] = useState<AdminProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  // Stats calculées depuis la liste affichée (account-aware) pour que les cartes
  // soient toujours alignées avec le nombre de lignes. Le détail (homeWithAccount
  // vs guestWithAccount) explicite la part d'invités de niveau compte d'une autre org.
  const cardStats = useMemo(() => {
    const withAccount = admins.filter(a => a.hasAccount && a.isActive);
    const guestWithAccount = withAccount.filter(a => a.isAccountLevel && a.isHomeOrg === false);
    return {
      total: admins.length,
      withAccount: withAccount.length,
      withoutAccount: admins.filter(a => !a.hasAccount && !a.isSuperAdmin).length,
      superAdmins: admins.filter(a => a.isSuperAdmin).length,
      guestWithAccount: guestWithAccount.length,
      homeWithAccount: withAccount.length - guestWithAccount.length,
    };
  }, [admins]);

  // Catalogue des rôles fourni dynamiquement par le backend (RBAC configurable)
  const [adminRoles, setAdminRoles] = useState<RoleInfo[]>([]);
  const [allowedRoles, setAllowedRoles] = useState<RoleInfo[]>([]);
  const [defaultRoleName, setDefaultRoleName] = useState<string>('');

  // Resolution du libelle d'un role : catalogue dynamique > ROLE_LABELS legacy > nom brut
  const roleLabel = useCallback((roleName: string): string => {
    const fromCatalog = adminRoles.find(r => r.name === roleName)?.display_name;
    return fromCatalog || ROLE_LABELS[roleName] || roleName;
  }, [adminRoles]);

  // State modale création compte
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTarget, setCreateTarget] = useState<AdminProfile | null>(null);
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: ''
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);

  const getToken = () => localStorage.getItem('sso_token');

  const fetchAdmins = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError('Session expirée');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[AdminsPage] Chargement des administrateurs...');

      const adminsResponse = await fetch(`${API_BASE}/api/admins`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!adminsResponse.ok) {
        if (adminsResponse.status === 403) {
          throw new Error('Vous n\'êtes pas autorisé à accéder à cette page');
        }
        throw new Error(`Erreur HTTP ${adminsResponse.status}`);
      }

      const adminsResult = await adminsResponse.json();

      if (adminsResult.success) {
        setAdmins(adminsResult.admins || []);
        if (Array.isArray(adminsResult.roles)) setAdminRoles(adminsResult.roles);
        if (Array.isArray(adminsResult.allowedRoles)) setAllowedRoles(adminsResult.allowedRoles);
        if (typeof adminsResult.defaultRoleName === 'string') {
          setDefaultRoleName(adminsResult.defaultRoleName);
        }
        console.log(`[AdminsPage] ${adminsResult.count} utilisateur(s) chargé(s) — ${(adminsResult.allowedRoles || []).length} rôle(s) assignable(s)`);
      } else {
        throw new Error(adminsResult.error?.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('[AdminsPage] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  function openCreateModal(admin: AdminProfile) {
    setCreateTarget(admin);
    // Pré-remplissage prénom/nom : on privilégie les champs explicites s'ils existent,
    // sinon on découpe le nom complet (company_name → nomComplet) issu de l'IBM i.
    const fallbackName = splitFullName(admin.nomComplet);
    setCreateForm({
      email: admin.email || '',
      first_name: admin.firstName || fallbackName.first,
      last_name: admin.lastName || fallbackName.last,
      phone: admin.phone || '',
      role: defaultRoleName || (allowedRoles[0]?.name ?? '')
    });
    setCreateError(null);
    setShowCreateModal(true);
  }

  function closeCreateModal() {
    if (createLoading) return;
    setShowCreateModal(false);
    setCreateTarget(null);
    setCreateError(null);
  }

  async function submitCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    if (!createTarget) return;

    setCreateError(null);

    if (!createForm.email) {
      setCreateError('L\'email est obligatoire.');
      return;
    }
    // Telephone FACULTATIF depuis la v1.8 (L1bis.5) : l'admin recoit un lien par email ;
    // s'il est saisi, il doit etre valide (il servira au « Mot de passe oublie », OTP SMS).
    if (createForm.phone && !isValidPhoneNumber(createForm.phone)) {
      setCreateError('Numéro de téléphone invalide.');
      return;
    }
    if (!createForm.role) {
      setCreateError('Le rôle est obligatoire.');
      return;
    }

    const token = getToken();
    if (!token) {
      setCreateError('Session expirée.');
      return;
    }

    setCreateLoading(true);
    try {
      const body: Record<string, unknown> = {
        email: createForm.email.trim().toLowerCase(),
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        phone: createForm.phone || undefined,
        role: createForm.role
      };

      const response = await fetch(`${API_BASE}/api/admins/${createTarget.login}/create-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!result.success) {
        setCreateError(result.error?.message || `Erreur ${response.status}`);
        return;
      }

      const actionLabel = result.action === 'reactivated' ? 'réactivé' : (result.action === 'linked' ? 'rattaché' : 'créé');
      const notif = result.notifications || {};
      const notifParts: string[] = [];
      // v1.8 (L1bis.5) : un admin recoit un LIEN « Definir mon mot de passe » par email ;
      // le SMS ne concerne plus que les techniciens.
      if (notif.set_password_link_sent) notifParts.push('lien « Définir mon mot de passe » envoyé par email, valable 7 jours');
      else if (notif.email_sent) notifParts.push('email envoyé');
      if (notif.sms_sent) notifParts.push('SMS envoyé');
      if (!notif.set_password_link_sent && !notif.sms_sent && !notif.email_sent) notifParts.push('aucune notification envoyée : renvoyez l’invitation');
      const notifText = notifParts.length > 0 ? ` (${notifParts.join(', ')})` : '';

      setSuccessMessage(`Compte ${actionLabel} pour ${createTarget.login}${notifText}.`);
      setShowCreateModal(false);
      setCreateTarget(null);
      await fetchAdmins();
    } catch (err) {
      console.error('[AdminsPage] Erreur création:', err);
      setCreateError(err instanceof Error ? err.message : 'Erreur de connexion au serveur');
    } finally {
      setCreateLoading(false);
    }
  }

  async function revokeAdmin(login: string) {
    const token = getToken();
    if (!token) return;
    if (!confirm(`Êtes-vous sûr de vouloir retirer l'accès à ${login} ?`)) return;

    setActionLoading(login);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admins/${login}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`L'accès de ${login} a été retiré`);
        await fetchAdmins();
      } else {
        throw new Error(result.error?.message || 'Erreur lors de la révocation');
      }
    } catch (err) {
      console.error('[AdminsPage] Erreur révocation:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la révocation');
    } finally {
      setActionLoading(null);
    }
  }

  useEffect(() => {
    if (canView) fetchAdmins();
  }, [canView, fetchAdmins]);

  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (canView) fetchAdmins();
  }, [refreshTrigger, fetchAdmins, canView]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = searchTerm === '' ||
      admin.login.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.nomComplet.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (admin.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'with_account' && admin.hasAccount && admin.isActive) ||
      (filterStatus === 'without_account' && !admin.hasAccount && !admin.isSuperAdmin) ||
      (filterStatus === 'superadmin' && admin.isSuperAdmin);

    return matchesSearch && matchesStatus;
  });

  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '16px' }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Styles pour PhoneInput */}
      <style>{phoneInputStyles}</style>

      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isPhone ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isPhone ? 'stretch' : 'center',
        gap: isPhone ? 12 : 0,
        marginBottom: '24px'
      }}>
        <div>
          <p style={{ color: '#64748b', margin: 0 }}>
            Créez des comptes d'administration à partir des utilisateurs IBM i synchronisés.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: isPhone ? 'wrap' : 'nowrap' }}>
          <button
            onClick={fetchAdmins}
            disabled={loading}
            title="Actualiser les données"
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569',
              border: '1px solid #e2e8f0', borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500', transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          >
            <RefreshIcon size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
          backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '8px', marginBottom: '16px', color: '#dc2626'
        }}>
          <CloseIcon size={20} color="#dc2626" />
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <CloseIcon size={16} color="#dc2626" />
          </button>
        </div>
      )}

      {successMessage && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
          backgroundColor: '#dcfce7', border: '1px solid #86efac',
          borderRadius: '8px', marginBottom: '16px', color: '#166534'
        }}>
          <CheckIcon size={20} color="#166534" />
          {successMessage}
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isPhone ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isPhone ? '12px' : '16px',
        marginBottom: '24px'
      }}>
        <StatCard
          icon={<UserIcon size={24} color="#3b82f6" />}
          label="Utilisateurs IBM i"
          value={cardStats.total}
          bgColor="#dbeafe"
          accentColor="#3b82f6"
          active={filterStatus === 'all'}
          onClick={() => setFilterStatus('all')}
        />
        <StatCard
          icon={<CheckIcon size={24} color="#10b981" />}
          label="Avec compte"
          value={cardStats.withAccount}
          bgColor="#dcfce7"
          accentColor="#10b981"
          active={filterStatus === 'with_account'}
          onClick={() => setFilterStatus(filterStatus === 'with_account' ? 'all' : 'with_account')}
        />
        <StatCard
          icon={<UserPlusIcon size={24} color="#f59e0b" />}
          label="Sans compte"
          value={cardStats.withoutAccount}
          bgColor="#fef3c7"
          accentColor="#f59e0b"
          active={filterStatus === 'without_account'}
          onClick={() => setFilterStatus(filterStatus === 'without_account' ? 'all' : 'without_account')}
        />
        <StatCard
          icon={<ShieldIcon size={24} color="#8b5cf6" />}
          label="Super admins"
          value={cardStats.superAdmins}
          bgColor="#ede9fe"
          accentColor="#8b5cf6"
          active={filterStatus === 'superadmin'}
          onClick={() => setFilterStatus(filterStatus === 'superadmin' ? 'all' : 'superadmin')}
        />
      </div>

      {/* Détail account-aware : explicite la composition du compteur « Avec compte » */}
      {filterStatus === 'with_account' && cardStats.guestWithAccount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 14px', marginBottom: '16px',
          backgroundColor: '#eef2ff', border: '1px solid #c7d2fe',
          borderRadius: '8px', color: '#4338ca', fontSize: '13px'
        }}>
          <ShieldIcon size={16} color="#6366f1" />
          <span>
            <strong>{cardStats.withAccount}</strong> compte(s) avec accès ={' '}
            <strong>{cardStats.homeWithAccount}</strong> de cette organisation +{' '}
            <strong>{cardStats.guestWithAccount}</strong> de niveau compte (autre organisation, lecture seule)
          </span>
        </div>
      )}

      {/* Recherche */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <SearchIcon
            size={18}
            color="#94a3b8"
            style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
          />
          <input
            type="text"
            placeholder="Rechercher par identifiant, nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 40px',
              border: '1px solid #e2e8f0', borderRadius: '8px',
              fontSize: '14px', outline: 'none', boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            <RefreshIcon size={32} color="#94a3b8" style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <div>Chargement...</div>
          </div>
        ) : filteredAdmins.length === 0 && !isPhone ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            <UserIcon size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
            <div style={{ marginBottom: '8px' }}>Aucun utilisateur trouvé</div>
            <p style={{ fontSize: '13px', margin: 0 }}>
              {filterStatus !== 'all' ? 'Essayez de modifier les filtres' : 'Aucun utilisateur dans la base'}
            </p>
          </div>
        ) : (
          <ResponsiveCardList
            items={filteredAdmins}
            renderCard={(admin) => (
              <AdminMobileCard
                admin={admin}
                actionLoading={actionLoading}
                onRevoke={revokeAdmin}
                onCreate={openCreateModal}
                canCreateAdmin={canCreateAdmin}
                canRevoke={canRevoke}
                roleLabel={roleLabel}
              />
            )}
            fallback={
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={thStyle}>Identifiant</th>
                    <th style={thStyle}>Nom</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Statut</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdmins.map((admin, index) => (
                    <tr
                      key={admin.login}
                      style={{
                        backgroundColor: admin.isSuperAdmin
                          ? '#fef3c7'
                          : (index % 2 === 0 ? 'white' : '#f8fafc')
                      }}
                    >
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '36px', height: '36px',
                            backgroundColor: admin.isSuperAdmin
                              ? '#fde68a'
                              : admin.hasAccount
                                ? '#dcfce7'
                                : '#f1f5f9',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {admin.isSuperAdmin
                              ? <ShieldIcon size={18} color="#d97706" />
                              : admin.hasAccount
                                ? <CheckIcon size={18} color="#10b981" />
                                : <UserIcon size={18} color="#94a3b8" />}
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{admin.login}</div>
                            {admin.isSuperAdmin && (
                              <span style={{ fontSize: '11px', color: '#92400e', fontWeight: '500' }}>
                                Super administrateur
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: '#475569', fontSize: '14px' }}>
                          {admin.nomComplet || '-'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: '#475569', fontSize: '13px' }}>
                          {admin.email || <span style={{ color: '#94a3b8' }}>—</span>}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {admin.isSuperAdmin ? (
                          <StatusBadge color="#92400e" bg="#fef3c7" icon={<ShieldIcon size={12} />} label="Toujours autorisé" />
                        ) : admin.hasAccount && admin.isActive ? (
                          <StatusBadge color="#166534" bg="#dcfce7" icon={<CheckIcon size={12} />} label={`Compte (${roleLabel(admin.role)})`} />
                        ) : admin.hasAccount && !admin.isActive ? (
                          <StatusBadge color="#92400e" bg="#fef3c7" icon={<CloseIcon size={12} />} label="Désactivé" />
                        ) : (
                          <StatusBadge color="#475569" bg="#f1f5f9" icon={<UserPlusIcon size={12} />} label="Sans compte" />
                        )}
                        {admin.isAccountLevel && admin.isHomeOrg === false && admin.organizationName && (
                          <div style={{ marginTop: 4, fontSize: '11px', color: '#6366f1', fontWeight: 500 }}>
                            Admin compte · {admin.organizationName}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {admin.isSuperAdmin ? (
                          <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                        ) : admin.hasAccount && admin.isActive ? (
                          (canRevoke && !(admin.isAccountLevel && admin.isHomeOrg === false)) ? (
                            <button
                              onClick={() => revokeAdmin(admin.login)}
                              disabled={actionLoading === admin.login}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 12px', backgroundColor: '#fef2f2', color: '#dc2626',
                                border: '1px solid #fecaca', borderRadius: '6px',
                                cursor: actionLoading === admin.login ? 'not-allowed' : 'pointer',
                                fontSize: '13px', fontWeight: '500',
                                opacity: actionLoading === admin.login ? 0.5 : 1
                              }}
                            >
                              <TrashIcon size={14} />
                              Retirer l'accès
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                          )
                        ) : (
                          canCreateAdmin ? (
                            <button
                              onClick={() => openCreateModal(admin)}
                              disabled={actionLoading === admin.login}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 12px', backgroundColor: '#dbeafe', color: '#1e40af',
                                border: '1px solid #93c5fd', borderRadius: '6px',
                                cursor: actionLoading === admin.login ? 'not-allowed' : 'pointer',
                                fontSize: '13px', fontWeight: '500',
                                opacity: actionLoading === admin.login ? 0.5 : 1
                              }}
                            >
                              <PlusIcon size={14} />
                              {admin.hasAccount && !admin.isActive ? 'Réactiver le compte' : 'Créer un compte'}
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
            emptyMessage="Aucun administrateur ne correspond aux filtres."
          />
        )}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '16px', padding: '16px',
        backgroundColor: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: '8px', fontSize: '13px', color: '#0369a1'
      }}>
        <strong>Comment ça fonctionne :</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          <li>
            <strong>Sans compte</strong> : utilisateur IBM i synchronisé, aucun compte Supabase associé. Cliquez sur « Créer un compte » pour lui envoyer par email un lien « Définir mon mot de passe » (valable 7 jours).
          </li>
          <li><strong>Avec compte</strong> : compte actif, l'administrateur peut se connecter via son email et son mot de passe.</li>
          <li>
            <strong>Super administrateurs</strong> : toujours autorisés, ne peuvent pas être révoqués
            {(() => {
              const superLogins = admins.filter(a => a.isSuperAdmin).map(a => a.login);
              return superLogins.length > 0 ? (
                <span style={{ marginLeft: '8px', fontFamily: 'monospace' }}>
                  [{superLogins.join(', ')}]
                </span>
              ) : null;
            })()}
          </li>
        </ul>
      </div>

      {/* Modale création compte */}
      {showCreateModal && createTarget && (
        <div
          onClick={closeCreateModal}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1001, padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '12px',
              padding: isPhone ? '16px' : '24px',
              width: '100%',
              maxWidth: isPhone ? 'calc(100vw - 24px)' : '480px',
              maxHeight: '90vh', overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: '0 0 4px' }}>
                  Créer un compte administrateur
                </h2>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Identifiant IBM i : <strong style={{ color: '#1e293b' }}>{createTarget.login}</strong>
                </p>
              </div>
              <button
                onClick={closeCreateModal}
                disabled={createLoading}
                type="button"
                style={{
                  background: 'none', border: 'none',
                  cursor: createLoading ? 'not-allowed' : 'pointer',
                  padding: '4px', color: '#64748b'
                }}
              >
                <CloseIcon size={20} color="#64748b" />
              </button>
            </div>

            {createError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', marginBottom: '16px',
                color: '#dc2626', fontSize: '14px'
              }}>
                {createError}
              </div>
            )}

            <form onSubmit={submitCreateAccount}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <FormField
                  label="Prénom"
                  value={createForm.first_name}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, first_name: v }))}
                />
                <FormField
                  label="Nom"
                  value={createForm.last_name}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, last_name: v }))}
                />
              </div>

              <FormField
                label="Email *"
                type="email"
                value={createForm.email}
                onChange={(v) => setCreateForm(prev => ({ ...prev, email: v }))}
                required
              />

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Téléphone portable (facultatif)
                </label>
                <PhoneInput
                  international
                  defaultCountry="GP"
                  value={createForm.phone}
                  onChange={(value) => setCreateForm(prev => ({ ...prev, phone: value || '' }))}
                  placeholder="0690 12 34 56"
                />
                {createForm.phone && !isValidPhoneNumber(createForm.phone) && (
                  <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>
                    Numéro de téléphone invalide
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  Sert à « Mot de passe oublié » (code par SMS). Sans téléphone, la réinitialisation passe par « Renvoyer l’invitation ».
                </p>
              </div>

              {allowedRoles.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Rôle *
                  </label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 12px',
                      border: '1px solid #d1d5db', borderRadius: '8px',
                      fontSize: '14px', backgroundColor: 'white',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  >
                    {allowedRoles.map(r => (
                      <option key={r.name} value={r.name}>
                        {r.display_name || ROLE_LABELS[r.name] || r.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{
                padding: '12px',
                backgroundColor: '#f0f9ff', borderRadius: '8px',
                marginBottom: '16px', fontSize: '13px', color: '#0369a1', lineHeight: 1.5
              }}>
                Un email sera envoyé à l'administrateur avec un lien « Définir mon mot de passe » valable 7 jours, à usage unique. Aucun mot de passe temporaire n'est transmis.
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={createLoading}
                  style={{
                    flex: 1, padding: '12px',
                    backgroundColor: '#f1f5f9', color: '#475569',
                    border: '1px solid #e2e8f0', borderRadius: '8px',
                    fontSize: '14px', fontWeight: '500',
                    cursor: createLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    flex: 1, padding: '12px',
                    backgroundColor: createLoading ? '#93c5fd' : '#3b82f6', color: 'white',
                    border: 'none', borderRadius: '8px',
                    fontSize: '14px', fontWeight: '500',
                    cursor: createLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}
                >
                  {createLoading ? (
                    <>
                      <div style={{
                        width: '16px', height: '16px',
                        border: '2px solid white', borderTop: '2px solid transparent',
                        borderRadius: '50%', animation: 'spin 1s linear infinite'
                      }}></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <UserPlusIcon size={16} />
                      Créer le compte
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant : champ de formulaire
interface FormFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}
function FormField({ label, value, onChange, type = 'text', required }: FormFieldProps) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={{
          width: '100%', padding: '10px 12px',
          border: '1px solid #d1d5db', borderRadius: '8px',
          fontSize: '14px', outline: 'none', boxSizing: 'border-box',
          transition: 'border-color 0.15s'
        }}
        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  );
}

// Composant : carte de stats
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  bgColor: string;
  accentColor?: string;
  active?: boolean;
  onClick?: () => void;
}
function StatCard({ icon, label, value, bgColor, accentColor, active, onClick }: StatCardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '20px',
        border: active ? `2px solid ${accentColor || '#3b82f6'}` : '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', gap: '16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        boxShadow: active ? `0 0 0 1px ${accentColor || '#3b82f6'}20` : 'none'
      }}
    >
      <div style={{
        width: '48px', height: '48px',
        backgroundColor: bgColor, borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '28px', fontWeight: '600', color: '#1e293b' }}>{value}</div>
        <div style={{ fontSize: '14px', color: active ? (accentColor || '#3b82f6') : '#64748b', fontWeight: active ? '600' : '400' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '14px 20px', textAlign: 'left',
  fontSize: '13px', fontWeight: '600',
  color: '#475569', borderBottom: '1px solid #e2e8f0'
};

const tdStyle: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontSize: '14px'
};

export default AdminsPage;
